import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Package, Plus, Pencil, Trash2, AlertTriangle, Search,
  Box, DollarSign, Archive, RefreshCw, Minus, X
} from 'lucide-react';

export default function AdminKitsPage({ embedded = false }) {
  const { api } = useAuth();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState({ kit: null, amount: 0, reason: '' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    stock_quantity: '',
    reorder_threshold: '10',
    weight_oz: '',
    image_url: '',
    components: [],
    active: true,
  });
  const [newComponent, setNewComponent] = useState('');

  useEffect(() => {
    fetchKits();
  }, []);

  const fetchKits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kits/?active_only=false');
      setKits(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to load kits');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        reorder_threshold: parseInt(formData.reorder_threshold) || 10,
        weight_oz: parseFloat(formData.weight_oz) || 0,
      };

      if (editingKit) {
        await api.put(`/kits/${editingKit.id}/`, data);
        toast.success('Kit updated successfully');
      } else {
        await api.post('/kits/', data);
        toast.success('Kit created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save kit');
    }
  };

  const handleDelete = async (kit) => {
    if (!window.confirm(`Delete kit "${kit.name}"?`)) return;
    try {
      await api.delete(`/kits/${kit.id}/`);
      toast.success('Kit deleted');
      fetchKits();
    } catch (error) {
      toast.error('Failed to delete kit');
    }
  };

  const handleStockAdjust = async () => {
    try {
      await api.post(`/kits/${stockAdjustment.kit.id}/adjust_stock/`, {
        adjustment: stockAdjustment.amount,
        reason: stockAdjustment.reason,
      });
      toast.success('Stock adjusted successfully');
      setShowStockDialog(false);
      setStockAdjustment({ kit: null, amount: 0, reason: '' });
      fetchKits();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  const openEditDialog = (kit) => {
    setEditingKit(kit);
    setFormData({
      name: kit.name,
      description: kit.description || '',
      price: kit.price.toString(),
      sku: kit.sku || '',
      stock_quantity: kit.stock_quantity.toString(),
      reorder_threshold: kit.reorder_threshold.toString(),
      weight_oz: kit.weight_oz?.toString() || '',
      image_url: kit.image_url || '',
      components: kit.components || [],
      active: kit.active,
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingKit(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      sku: '',
      stock_quantity: '',
      reorder_threshold: '10',
      weight_oz: '',
      image_url: '',
      components: [],
      active: true,
    });
    setNewComponent('');
  };

  const addComponent = () => {
    if (newComponent.trim()) {
      setFormData(prev => ({
        ...prev,
        components: [...prev.components, newComponent.trim()],
      }));
      setNewComponent('');
    }
  };

  const removeComponent = (index) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index),
    }));
  };

  const filteredKits = kits.filter(kit =>
    kit.name.toLowerCase().includes(search.toLowerCase()) ||
    kit.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStockKits = kits.filter(k => k.is_low_stock);
  const totalValue = kits.reduce((sum, k) => sum + (parseFloat(k.price) * k.stock_quantity), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Product Management</h1>
          <p className="text-muted-foreground">Manage inventory and kit details</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-kit-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Kit
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Package className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{kits.length}</p>
                <p className="text-sm text-muted-foreground">Total Kits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Archive className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{kits.reduce((sum, k) => sum + k.stock_quantity, 0)}</p>
                <p className="text-sm text-muted-foreground">Total Units</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{lowStockKits.length}</p>
                <p className="text-sm text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">${totalValue.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">Inventory Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStockKits.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lowStockKits.map(kit => (
                <Badge key={kit.id} variant="outline" className="bg-white text-amber-700">
                  {kit.name}: {kit.stock_quantity} left (reorder at {kit.reorder_threshold})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search kits..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-kits"
          />
        </div>
        <Button variant="outline" onClick={fetchKits}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Kits Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kit Name</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredKits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">No kits found</TableCell>
                </TableRow>
              ) : (
                filteredKits.map(kit => (
                  <TableRow key={kit.id} data-testid={`kit-row-${kit.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {kit.image_url ? (
                          <img src={kit.image_url} alt={kit.name} className="h-10 w-10 rounded object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{kit.name}</p>
                          {kit.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{kit.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{kit.sku || '-'}</TableCell>
                    <TableCell className="font-medium">${parseFloat(kit.price).toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={kit.is_low_stock ? 'text-amber-600 font-medium' : ''}>
                          {kit.stock_quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setStockAdjustment({ kit, amount: 0, reason: '' });
                            setShowStockDialog(true);
                          }}
                          data-testid={`adjust-stock-${kit.id}`}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {kit.components?.slice(0, 3).map((c, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>
                        ))}
                        {kit.components?.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{kit.components.length - 3}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {!kit.active ? (
                        <Badge variant="secondary">Inactive</Badge>
                      ) : kit.is_low_stock ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">Low Stock</Badge>
                      ) : kit.is_in_stock ? (
                        <Badge variant="outline" className="text-green-600 border-green-300">In Stock</Badge>
                      ) : (
                        <Badge variant="destructive">Out of Stock</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(kit)} data-testid={`edit-kit-${kit.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(kit)} className="text-destructive" data-testid={`delete-kit-${kit.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingKit ? 'Edit Kit' : 'Create New Kit'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Kit Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Arduino Starter Kit"
                  data-testid="kit-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Complete kit with all components needed for the course..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="49.99"
                  data-testid="kit-price-input"
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="KIT-ARD-001"
                />
              </div>
              <div>
                <Label>Stock Quantity</Label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                  placeholder="50"
                />
              </div>
              <div>
                <Label>Reorder Threshold</Label>
                <Input
                  type="number"
                  value={formData.reorder_threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, reorder_threshold: e.target.value }))}
                  placeholder="10"
                />
              </div>
              <div>
                <Label>Weight (oz)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight_oz}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_oz: e.target.value }))}
                  placeholder="16"
                />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Components Editor */}
            <div>
              <Label>Kit Components</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newComponent}
                  onChange={(e) => setNewComponent(e.target.value)}
                  placeholder="Add component (e.g., Arduino Uno)"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addComponent())}
                />
                <Button type="button" onClick={addComponent} variant="secondary">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.components.map((comp, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1 py-1">
                    {comp}
                    <button onClick={() => removeComponent(index)} className="ml-1 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
              />
              <Label htmlFor="active">Active (available for sale)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-kit-btn">
              {editingKit ? 'Update Kit' : 'Create Kit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock - {stockAdjustment.kit?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Current Stock</p>
              <p className="text-3xl font-bold">{stockAdjustment.kit?.stock_quantity}</p>
            </div>
            <div>
              <Label>Adjustment Amount</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setStockAdjustment(prev => ({ ...prev, amount: prev.amount - 1 }))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={stockAdjustment.amount}
                  onChange={(e) => setStockAdjustment(prev => ({ ...prev, amount: parseInt(e.target.value) || 0 }))}
                  className="text-center"
                  data-testid="stock-adjustment-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setStockAdjustment(prev => ({ ...prev, amount: prev.amount + 1 }))}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1 text-center">
                New stock will be: {(stockAdjustment.kit?.stock_quantity || 0) + stockAdjustment.amount}
              </p>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={stockAdjustment.reason}
                onChange={(e) => setStockAdjustment(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Received shipment, sold, damaged, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStockDialog(false)}>Cancel</Button>
            <Button onClick={handleStockAdjust} data-testid="confirm-stock-adjust">
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
